import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { LOGO_URL } from "@/lib/useReveal";
import { SCRIPTED, HELP_TIPS, COMPLETION, EVOLVE } from "@/demo/evolveData";
import { askDemo, submitLead, getDemoMoneyMeter, simulateDemoMoneyMeter, scanDemo } from "@/demo/demoApi";
import { MoneyMeterDelta, MoneyMeterPanel, bandColor } from "@/user/ceo/MoneyMeter";
import { Modal } from "@/admin/ui";

const MAX_Q = 6;

function mdInline(text = "") {
    return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
        p.startsWith("**") && p.endsWith("**")
            ? <strong key={i} className="font-semibold text-black">{p.slice(2, -2)}</strong>
            : <span key={i}>{p}</span>
    );
}
function Answer({ text }) {
    return (
        <div className="space-y-1.5 text-[14.5px] leading-relaxed text-neutral-800">
            {text.split("\n").filter(Boolean).map((line, i) => <p key={i}>{mdInline(line)}</p>)}
        </div>
    );
}

function burst() {
    confetti({ particleCount: 90, spread: 70, origin: { y: 0.6 }, colors: ["#8b5cf6", "#6366f1", "#a78bfa", "#c4b5fd"] });
}

export default function DemoChat() {
    const navigate = useNavigate();
    const name = sessionStorage.getItem("evolve_demo_name");
    const [msgs, setMsgs] = useState([]);
    const [remaining, setRemaining] = useState(SCRIPTED);
    const [used, setUsed] = useState(0);
    const [llmUsed, setLlmUsed] = useState(false);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [popup, setPopup] = useState(null);
    const [done, setDone] = useState(false);
    const [help, setHelp] = useState(false);
    const [notice, setNotice] = useState("");
    const [mm, setMm] = useState(null);
    const [showMM, setShowMM] = useState(false);
    const [scanOpen, setScanOpen] = useState(false);
    const endRef = useRef(null);

    useEffect(() => { if (!name) navigate("/demo"); }, [name, navigate]);
    useEffect(() => { getDemoMoneyMeter().then(setMm).catch(() => {}); }, []);
    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

    function pushUser(text) { setMsgs((m) => [...m, { id: Date.now() + "u", role: "user", content: text }]); }
    function pushAI(sr) { setMsgs((m) => [...m, { id: Date.now() + "a", role: "assistant", sr }]); }

    function afterAnswer(value) {
        const nextUsed = used + 1;
        setUsed(nextUsed);
        setTimeout(() => { setPopup({ ...value, last: nextUsed >= MAX_Q }); burst(); }, 400);
    }

    function sendScripted(item) {
        if (busy || used >= MAX_Q) return;
        setNotice("");
        pushUser(item.prompt);
        setRemaining((r) => r.filter((x) => x.id !== item.id));
        setBusy(true);
        setTimeout(() => {
            pushAI(item);
            setBusy(false);
            afterAnswer(item.value);
        }, 700);
    }

    async function sendCustom(e) {
        e?.preventDefault();
        const text = input.trim();
        if (!text || busy || used >= MAX_Q) return;
        if (llmUsed) { setNotice("You've used your 1 live AI question — try a suggested prompt to keep exploring."); return; }
        setNotice("");
        setInput("");
        pushUser(text);
        setBusy(true);
        try {
            const d = await askDemo({ session_id: sessionStorage.getItem("evolve_demo_session") || "", name, message: text });
            pushAI(d.structured_response);
            setLlmUsed(true);
            afterAnswer(d.value_delivered || { title: "Value Delivered", message: "CEO GPT combined every department into one recommendation.", time_saved: "2.5 Hours", departments: ["Finance", "Sales"], confidence: d.structured_response?.confidence_score || 90, impact: "High" });
        } catch (err) {
            const status = err?.response?.status;
            setNotice(status === 429 ? "You've used your 1 live AI question — try a suggested prompt." : "Something went wrong. Try a suggested prompt.");
            setLlmUsed(true);
        } finally { setBusy(false); }
    }

    async function runDemoScan(doc) {
        if (busy || used >= MAX_Q) return;
        setScanOpen(false);
        setNotice("");
        pushUser(`📎 Business Scan: ${doc.label}`);
        setBusy(true);
        try {
            const d = await scanDemo({ hint: doc.hint });
            setTimeout(() => {
                pushAI(d.structured_response);
                setBusy(false);
                afterAnswer({
                    title: "Business Scan Complete",
                    message: `CEO GPT scanned the ${doc.label.toLowerCase()} and generated a Decision Card.`,
                    time_saved: "45 Min", departments: d.department_detected || ["Finance"],
                    confidence: d.confidence_score || 90, impact: "High",
                });
            }, 1400);
        } catch (_e) {
            setBusy(false);
            setNotice("Scan failed. Try another document.");
        }
    }

    function closePopup() {
        const last = popup?.last;
        setPopup(null);
        if (last) { setTimeout(() => { setDone(true); burst(); }, 250); }
    }

    if (done) return <Completion name={name} onExit={() => navigate("/")} />;

    return (
        <div className="min-h-screen dot-grid relative flex flex-col" data-testid="demo-chat">
            {/* Header */}
            <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-black/5">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <img src={LOGO_URL} alt="" className="logo-blend h-8 w-8 object-contain shrink-0" />
                        <div className="min-w-0">
                            <div className="text-[14px] font-medium text-black truncate">{EVOLVE.name}</div>
                            <div className="text-[11px] text-neutral-500 truncate">CEO GPT · {name}</div>
                        </div>
                        <span data-testid="demo-badge" className="ml-1 hidden sm:inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.12em] uppercase text-violet-700 bg-violet-50 border border-violet-100 rounded-full px-2.5 py-1">
                            Demo Mode
                        </span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <button data-testid="demo-back-home" onClick={() => navigate("/")}
                            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-neutral-700 border border-black/10 rounded-full px-3 py-2 hover:bg-black/[0.03] transition-colors">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M19 12H5M11 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span className="hidden sm:inline">Back to Landing Page</span>
                        </button>
                        {mm && (
                            <button data-testid="demo-mm-chip" onClick={() => setShowMM(true)} title="Money Meter"
                                className="inline-flex items-center gap-1.5 text-[12px] font-medium rounded-full border border-black/10 px-2.5 py-1.5 hover:bg-black/[0.03]">
                                <span className="h-2 w-2 rounded-full" style={{ background: bandColor(mm.score) }} />
                                {Math.round(mm.score)}
                            </button>
                        )}
                        <button data-testid="demo-dashboard-btn" onClick={() => setShowMM(true)}
                            className="hidden sm:inline-flex items-center gap-1.5 text-[12.5px] font-medium text-black border border-black/10 rounded-full px-3.5 py-2 hover:bg-black/[0.03]">
                            Money Meter
                        </button>
                    </div>
                </div>
            </header>

            {/* Feed */}
            <main className="flex-1">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 space-y-6">
                    {/* Greeting */}
                    <div className="flex gap-3" data-testid="demo-greeting">
                        <img src={LOGO_URL} alt="" className="h-8 w-8 logo-blend shrink-0 mt-1" />
                        <div className="flex-1">
                            <div className="glass rounded-3xl rounded-tl-lg px-5 py-4 text-[14.5px] text-neutral-800 leading-relaxed">
                                Good morning, <strong className="font-semibold">{name}</strong>. Welcome to {EVOLVE.name}. I've already analyzed today's company activity across all 10 departments. How would you like to begin?
                            </div>
                        </div>
                    </div>

                    {msgs.map((m) => m.role === "user"
                        ? (
                            <div key={m.id} className="flex justify-end" data-testid="demo-msg-user">
                                <div className="max-w-[85%] bg-black text-white rounded-3xl rounded-br-lg px-5 py-3 text-[14.5px] leading-relaxed">{m.content}</div>
                            </div>
                        ) : <AIMessage key={m.id} sr={m.sr} onFollow={(q) => { setInput(q); }} />
                    )}

                    {busy && (
                        <div className="flex gap-3" data-testid="demo-typing">
                            <img src={LOGO_URL} alt="" className="h-8 w-8 logo-blend shrink-0 mt-1" />
                            <div className="glass rounded-3xl rounded-tl-lg px-5 py-4 flex gap-1.5">
                                {[0, 1, 2].map((i) => <span key={i} className="h-2 w-2 rounded-full bg-neutral-400 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />)}
                            </div>
                        </div>
                    )}

                    {/* Suggested prompts */}
                    {!busy && used < MAX_Q && remaining.length > 0 && (
                        <div className="flex flex-wrap gap-2" data-testid="demo-suggestions">
                            {remaining.map((s) => (
                                <button key={s.id} data-testid={`demo-suggestion-${s.id}`} onClick={() => sendScripted(s)}
                                    className="text-[13px] text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-100 rounded-full px-3.5 py-2 transition-colors">
                                    {s.prompt}
                                </button>
                            ))}
                        </div>
                    )}
                    <div ref={endRef} />
                </div>
            </main>

            {/* Composer */}
            <div className="sticky bottom-0 backdrop-blur-xl bg-white/70 border-t border-black/5">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 py-3">
                    {notice && <div className="mb-2 text-[12.5px] text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2" data-testid="demo-notice">{notice}</div>}
                    <form onSubmit={sendCustom} className="flex items-center gap-2">
                        <div className="relative">
                            <button type="button" data-testid="demo-scan-btn" onClick={() => setScanOpen((v) => !v)} disabled={busy || used >= MAX_Q}
                                title="Business Scan (demo)"
                                className="h-11 px-3 grid place-items-center rounded-full text-violet-700 bg-violet-50 border border-violet-100 hover:bg-violet-100 disabled:opacity-40 transition-colors">
                                <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 7V5a1 1 0 011-1h2M17 4h2a1 1 0 011 1v2M20 17v2a1 1 0 01-1 1h-2M7 20H5a1 1 0 01-1-1v-2M4 12h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    <span className="hidden sm:inline">Scan</span>
                                </span>
                            </button>
                            <AnimatePresence>
                                {scanOpen && (
                                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                                        data-testid="demo-scan-menu"
                                        className="absolute bottom-14 left-0 w-60 rounded-2xl bg-white border border-black/10 shadow-xl p-2 z-40">
                                        <div className="px-2 py-1.5 text-[10px] font-semibold tracking-[0.16em] uppercase text-neutral-400">Scan a sample document</div>
                                        {[{ label: "Invoice", hint: "invoice" }, { label: "Contract", hint: "contract" }, { label: "Warehouse Photo", hint: "warehouse" }, { label: "Marketing Dashboard", hint: "dashboard" }, { label: "Strategy Whiteboard", hint: "whiteboard" }].map((doc) => (
                                            <button key={doc.hint} type="button" data-testid={`demo-scan-${doc.hint}`} onClick={() => runDemoScan(doc)}
                                                className="w-full text-left px-3 py-2 rounded-xl text-[13px] text-neutral-700 hover:bg-violet-50 transition-colors">{doc.label}</button>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        <input data-testid="demo-input" value={input} onChange={(e) => setInput(e.target.value)}
                            disabled={used >= MAX_Q}
                            placeholder={llmUsed ? "Live AI question used — try a suggested prompt" : "Ask anything about EVOLVE… (1 live AI question)"}
                            className="flex-1 rounded-full border border-black/10 bg-white px-5 py-3 text-[14.5px] focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50" />
                        <button data-testid="demo-send" type="submit" disabled={busy || used >= MAX_Q || !input.trim()}
                            className="h-11 w-11 grid place-items-center rounded-full bg-black text-white disabled:opacity-40 hover:shadow-[0_10px_30px_-10px_rgba(139,92,246,0.7)] transition-all">↑</button>
                    </form>
                    <div className="mt-1.5 text-center text-[11px] text-neutral-400">Demo Mode · Mock company · No real actions executed</div>
                </div>
            </div>

            {/* Help launcher */}
            <button data-testid="demo-help-btn" onClick={() => setHelp((h) => !h)}
                className="fixed bottom-24 right-5 z-30 h-11 w-11 rounded-full bg-white border border-black/10 shadow-lg grid place-items-center text-violet-600 hover:scale-105 transition-transform">?</button>
            <AnimatePresence>
                {help && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                        className="fixed bottom-40 right-5 z-30 w-72 rounded-2xl bg-white border border-black/10 shadow-xl p-4" data-testid="demo-help-panel">
                        <div className="text-[13px] font-semibold text-black mb-2">How to use CEO GPT</div>
                        <ul className="space-y-1.5">{HELP_TIPS.map((t, i) => <li key={i} className="text-[12.5px] text-neutral-600 flex gap-2"><span className="text-violet-500">•</span>{t}</li>)}</ul>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Value popup */}
            <AnimatePresence>{popup && <ValuePopup v={popup} onClose={closePopup} />}</AnimatePresence>

            <Modal open={showMM} onClose={() => setShowMM(false)} title="Money Meter · EVOLVE Technologies" size="lg" testid="demo-mm-modal">
                <MoneyMeterPanel data={mm} onSimulate={(d) => simulateDemoMoneyMeter(d)} />
            </Modal>
        </div>
    );
}

function AIMessage({ sr, onFollow }) {
    if (!sr) return null;
    const money = sr.money_impact && typeof sr.money_impact === "object" ? Object.entries(sr.money_impact).filter(([k, v]) => k !== "is_estimate" && v) : [];
    const signals = sr.department_signals || [];
    const risks = sr.risks || [];
    const sigCls = (s) => /risk|weak|low|critical|churn/i.test(s) ? "text-rose-600 bg-rose-50" : /good|strong|healthy|high/i.test(s) ? "text-emerald-700 bg-emerald-50" : "text-neutral-600 bg-neutral-100";
    return (
        <div className="flex gap-3" data-testid="demo-msg-ai">
            <img src={LOGO_URL} alt="" className="h-8 w-8 logo-blend shrink-0 mt-1" />
            <div className="min-w-0 flex-1">
                <div className="glass rounded-3xl rounded-tl-lg px-5 py-4"><Answer text={sr.answer || ""} /></div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    {typeof sr.confidence_score === "number" && (
                        <span data-testid="demo-confidence" className="inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-700 bg-white border border-black/[0.07] rounded-full px-2.5 py-1">
                            <span className={`h-1.5 w-1.5 rounded-full ${sr.confidence_score >= 70 ? "bg-emerald-500" : sr.confidence_score >= 50 ? "bg-amber-500" : "bg-rose-500"}`} />
                            Confidence {sr.confidence_score}/100 · {sr.confidence_label}
                        </span>
                    )}
                    {typeof sr.money_meter?.current === "number" && (
                        <span data-testid="demo-money-meter" className="inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-700 bg-white border border-black/[0.07] rounded-full px-2.5 py-1">
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: bandColor(sr.money_meter.current) }} />
                            Money Meter {sr.money_meter.current} · {sr.money_meter.label}
                        </span>
                    )}
                    {signals.slice(0, 6).map((s, i) => {
                        const dep = typeof s === "object" ? s.department : "";
                        const sig = typeof s === "object" ? (s.signal || s.status) : s;
                        return <span key={i} className={`text-[11px] rounded-full px-2.5 py-1 ${sigCls(typeof s === "object" ? `${s.signal} ${s.status}` : s)}`}>{dep ? <b className="font-semibold">{dep}: </b> : null}{sig}</span>;
                    })}
                </div>
                {risks.length > 0 && (
                    <div className="mt-2 rounded-2xl border border-rose-100 bg-rose-50/50 p-3">
                        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-rose-500 mb-1">Risks</div>
                        <ul className="space-y-0.5">{risks.slice(0, 5).map((r, i) => <li key={i} className="text-[12.5px] text-rose-800 flex gap-2"><span>•</span>{typeof r === "object" ? (r.risk || r.title) : r}</li>)}</ul>
                    </div>
                )}
                {(sr.decision_cards || []).map((card, i) => <DemoCard key={i} card={card} />)}
                {money.length > 0 && (
                    <div className="mt-2 rounded-2xl border border-black/[0.06] bg-neutral-50/60 p-3">
                        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400 mb-1.5">Money Impact {sr.money_impact?.is_estimate ? <span className="normal-case tracking-normal">(estimate)</span> : null}</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {money.map(([k, v]) => <div key={k}><div className="text-[10px] text-neutral-400 capitalize">{k.replace(/_/g, " ")}</div><div className="text-[13px] font-medium text-neutral-800">{String(v)}</div></div>)}
                        </div>
                    </div>
                )}
                {(sr.follow_up_questions || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {sr.follow_up_questions.map((q, i) => (
                            <button key={i} onClick={() => onFollow(q)} className="text-[12.5px] text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-full px-3 py-1.5 transition-colors">{q}</button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function DemoCard({ card }) {
    const [state, setState] = useState("pending");
    const badge = state === "approved" ? ["APPROVED", "bg-emerald-50 text-emerald-700", "bg-emerald-500"]
        : state === "rejected" ? ["REJECTED", "bg-rose-50 text-rose-700", "bg-rose-500"]
            : ["PENDING", "bg-violet-50 text-violet-700", "bg-violet-500"];
    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -2 }}
            className="mt-3 rounded-3xl border border-black/[0.06] bg-white p-5 glow-purple-sm" data-testid="demo-decision-card">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-[10px] font-semibold tracking-[0.2em] uppercase text-violet-600">Decision Card{card.department ? ` · ${card.department}` : ""}</div>
                    <div className="mt-1 font-display text-xl font-medium text-black">{card.title}</div>
                </div>
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full ${badge[1]}`}><span className={`h-1.5 w-1.5 rounded-full ${badge[2]}`} />{badge[0]}</span>
            </div>
            {card.summary && <p className="mt-1.5 text-[13px] text-neutral-600 leading-relaxed">{card.summary}</p>}
            {(card.metrics || []).length > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-2">
                    {card.metrics.map((mt, i) => <div key={i} className="rounded-2xl bg-neutral-50/70 border border-black/5 p-2.5"><div className="text-[9.5px] tracking-[0.14em] uppercase text-neutral-500">{mt.label}</div><div className="mt-1 font-display text-lg text-violet-700">{mt.value}</div></div>)}
                </div>
            )}
            {typeof card.confidence === "number" && <div className="mt-3 text-[11px] text-neutral-500">Confidence {card.confidence}/100{card.expected_impact ? ` · Impact: ${card.expected_impact}` : ""}</div>}
            {card.money_meter && (card.money_meter.before != null || card.money_meter.after != null) && (
                <MoneyMeterDelta before={card.money_meter.before} after={card.money_meter.after} reason={card.money_meter.reason} />
            )}
            <div className="mt-4 flex flex-wrap gap-2">
                <button data-testid="demo-card-approve" onClick={() => setState("approved")} className="text-[13px] font-medium bg-black text-white px-4 py-2 rounded-full hover:bg-neutral-800 transition-colors">Approve</button>
                <button data-testid="demo-card-simulate" onClick={() => setState("pending")} className="text-[13px] font-medium bg-white text-black border border-black/10 px-4 py-2 rounded-full hover:bg-neutral-50 transition-colors">Simulate</button>
                <button data-testid="demo-card-reject" onClick={() => setState("rejected")} className="text-[13px] font-medium bg-white text-neutral-600 border border-black/10 px-4 py-2 rounded-full hover:bg-neutral-50 transition-colors">Reject</button>
            </div>
        </motion.div>
    );
}

function ValuePopup({ v, onClose }) {
    return (
        <div className="fixed inset-0 z-[120] grid place-items-center p-4" data-testid="demo-value-popup">
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
            <motion.div initial={{ opacity: 0, scale: 0.94, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
                className="relative w-full max-w-md rounded-[28px] bg-white p-7 shadow-[0_40px_120px_-30px_rgba(139,92,246,0.6)] border border-violet-100">
                <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-violet-600">Value Delivered</div>
                <h3 className="mt-1.5 font-display text-2xl font-medium text-black">{v.title}</h3>
                <p className="mt-2 text-[13.5px] text-neutral-600 leading-relaxed">{v.message}</p>
                <div className="mt-5 grid grid-cols-3 gap-2.5">
                    <div className="rounded-2xl bg-violet-50 p-3 text-center"><div className="font-display text-lg text-violet-700">{v.time_saved}</div><div className="text-[10px] uppercase tracking-wide text-neutral-500 mt-0.5">Time Saved</div></div>
                    <div className="rounded-2xl bg-emerald-50 p-3 text-center"><div className="font-display text-lg text-emerald-700">{v.confidence}%</div><div className="text-[10px] uppercase tracking-wide text-neutral-500 mt-0.5">Confidence</div></div>
                    <div className="rounded-2xl bg-neutral-100 p-3 text-center"><div className="font-display text-lg text-black">{v.impact}</div><div className="text-[10px] uppercase tracking-wide text-neutral-500 mt-0.5">Impact</div></div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {(v.departments || []).map((d) => <span key={d} className="text-[11px] text-neutral-600 bg-neutral-100 rounded-full px-2.5 py-1">{d}</span>)}
                </div>
                <button data-testid="demo-value-continue" onClick={onClose} className="mt-6 w-full rounded-2xl bg-black text-white font-medium py-3 hover:shadow-[0_16px_40px_-12px_rgba(139,92,246,0.7)] transition-all">
                    {v.last ? "See my results →" : "Continue"}
                </button>
            </motion.div>
        </div>
    );
}

function Completion({ name, onExit }) {
    const [email, setEmail] = useState("");
    const [interest, setInterest] = useState("book_demo");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);
    useEffect(() => { const t = setTimeout(burst, 300); return () => clearTimeout(t); }, []);

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            await submitLead({ name, email, interest, session_id: sessionStorage.getItem("evolve_demo_session") || "" });
            setSent(true); burst();
        } catch (_e) { setSent(true); } finally { setBusy(false); }
    }

    return (
        <div className="min-h-screen dot-grid flex items-center justify-center p-5" data-testid="demo-completion">
            <div className="w-full max-w-2xl rounded-[32px] glass-strong p-8 sm:p-10 text-center">
                <div className="text-[11px] font-semibold tracking-[0.24em] uppercase text-violet-600">Experience Complete</div>
                <h2 className="mt-2 font-display text-4xl font-light text-black">Congratulations, <span className="italic font-normal">{name}</span></h2>
                <p className="mt-2 text-[14px] text-neutral-600">You've completed the CEO's GPT experience for {EVOLVE.name}.</p>

                <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {COMPLETION.stats.map(([v, k]) => (
                        <div key={k} className="rounded-2xl border border-black/5 bg-white/70 p-3"><div className="font-display text-xl text-violet-700">{v}</div><div className="text-[10px] uppercase tracking-wide text-neutral-500 mt-0.5">{k}</div></div>
                    ))}
                </div>

                <div className="mt-6 flex flex-wrap justify-center gap-1.5">
                    {COMPLETION.experienced.map((x) => <span key={x} className="text-[11.5px] text-neutral-600 bg-neutral-100 rounded-full px-3 py-1">{x}</span>)}
                </div>

                {!sent ? (
                    <form onSubmit={submit} className="mt-8 max-w-md mx-auto text-left">
                        <div className="text-[13px] font-medium text-black mb-2">Bring CEO's GPT to your company</div>
                        <div className="flex flex-wrap gap-2 mb-2">
                            {[["book_demo", "Book a Demo"], ["request", "Request CEO GPT"], ["early_access", "Early Access"]].map(([val, label]) => (
                                <button type="button" key={val} onClick={() => setInterest(val)} data-testid={`demo-interest-${val}`}
                                    className={`text-[12.5px] rounded-full px-3 py-1.5 border transition-colors ${interest === val ? "bg-black text-white border-black" : "bg-white text-neutral-700 border-black/10 hover:bg-neutral-50"}`}>{label}</button>
                            ))}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input data-testid="demo-lead-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                                className="flex-1 rounded-full border border-black/10 bg-white px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" />
                            <button data-testid="demo-lead-submit" type="submit" disabled={busy}
                                className="rounded-full bg-black text-white text-sm font-medium px-6 py-3 hover:shadow-[0_14px_40px_-12px_rgba(139,92,246,0.7)] transition-all disabled:opacity-50">{busy ? "Sending…" : "Get Access"}</button>
                        </div>
                    </form>
                ) : (
                    <div className="mt-8 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-800 px-5 py-4 text-[14px]" data-testid="demo-lead-success">
                        Thank you, {name}! We'll be in touch shortly about bringing CEO's GPT to your company.
                    </div>
                )}

                <button data-testid="demo-return-home" onClick={onExit} className="mt-6 text-[13px] text-neutral-500 hover:text-black transition-colors">← Return to landing page</button>
            </div>
        </div>
    );
}
